import { createRouter, createWebHistory, type RouteLocationNormalized } from "vue-router"
import { toast } from "vue-sonner"
import { formatSlug } from "@auger/ui/utils/string"
import auth from "@/identity/auth"
import userState from "@/identity/currentUser"
import { isWaitlisted, WAITLIST_PATH } from "@/identity/waitlist"
import { startCatalog } from "@/init/catalog"
import { useUserStore } from "@/stores/user"
import { useUsersStore } from "@/stores/users"

declare module "vue-router" {
  interface RouteMeta {
    /** The page title, or one built from the route it's for. */
    title?: string | ((to: RouteLocationNormalized) => string)
    /** A Heavybit Identity page: no Auger session, stores or chrome. */
    identity?: boolean
    /** The page fills the viewport and scrolls inside itself. */
    fixedLayout?: boolean
    /** An /admin child's entry in the admin nav. */
    nav?: { label: string; icon: string }
  }
}

// A slug param as a title. None of these routes repeat a param, so an array
// never reaches here; its first value stands in if one does.
const slugTitle = (param: string | string[] | undefined) =>
  formatSlug(Array.isArray(param) ? param[0] : param)

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // ---- Utility routes ----
    // The old Auth0 redirect target. Bookmarks and browser history still hold
    // it, and with no catch-all route an unmatched path renders a blank shell.
    // The stale code/state query goes too.
    { path: "/callback", redirect: () => ({ path: "/", query: {}, hash: "" }) },
    {
      path: "/kaboom",
      component: () => import("@/views/TestExceptions.vue"),
      meta: { title: "Test Exceptions" },
    },
    // ---- Heavybit Identity (Stytch) front door ----
    // Brand-neutral sign-in, exchange, and Connected Apps consent pages for
    // the whole Stytch project. `meta.identity` makes the guard below skip
    // the sign-in redirect and the user stores; these pages have no Auger session.
    //
    // These URLs carry short-lived Stytch credentials in their query string
    // (the callback's discovery token, the authorize page's OAuth params,
    // login's return_to/origin_assertion) — PostHog is told to reduce any
    // /identity/* URL it captures down to origin + pathname before it is
    // sent, everywhere else is untouched. See @/init/posthogSanitize.ts.
    {
      path: "/identity/login",
      name: "identity-login",
      component: () => import("@/views/identity/IdentityLogin.vue"),
      meta: { identity: true },
    },
    {
      path: "/identity/callback",
      name: "identity-callback",
      component: () => import("@/views/identity/IdentityCallback.vue"),
      meta: { identity: true },
    },
    {
      path: "/identity/authorize",
      name: "identity-authorize",
      component: () => import("@/views/identity/IdentityAuthorize.vue"),
      meta: { identity: true },
    },
    {
      path: "/identity/done",
      name: "identity-done",
      component: () => import("@/views/identity/IdentityDone.vue"),
      meta: { identity: true },
    },
    // A successful sign-in with no access yet. Under /identity/* on purpose:
    // the guard below returns before any store loads, and App.vue renders no
    // Auger chrome, so a `network` user cannot reach code that calls Apollo.
    {
      path: WAITLIST_PATH,
      name: "identity-waitlist",
      component: () => import("@/views/identity/IdentityWaitlist.vue"),
      meta: { identity: true },
    },
    // ---- User-facing routes ----
    {
      path: "/",
      name: "root",
      component: () => import("@/views/ChatHome.vue"),
      meta: { title: "Home" },
    },
    {
      path: "/home",
      name: "home",
      component: () => import("@/views/UserHome.vue"),
      meta: { title: "Home" },
    },
    {
      path: "/inbox",
      name: "inbox",
      component: () => import("@/views/MailPage.vue"),
      meta: { title: "Inbox", fixedLayout: true },
    },
    {
      path: "/calendar",
      name: "calendar",
      component: () => import("@/views/ReportingCalendar.vue"),
      meta: { title: "Calendar" },
      props: (route) => ({
        category: route.query.category,
        start: route.query.start,
        calendarOverride: route.query.calendar,
      }),
    },
    {
      path: "/tasks",
      name: "tasks",
      component: () => import("@/views/TasksView.vue"),
      meta: { title: "Tasks" },
    },
    {
      path: "/events",
      component: () => import("@/views/events/EventsHome.vue"),
      // Every tab scrolls with the page, the tab row going with it.
      meta: { title: "Events" },
      children: [
        {
          path: "",
          name: "events-overview",
          component: () => import("@/views/events/EventsOverview.vue"),
          // Calendar links from before the tabs carried ?view= / ?date= on /events.
          beforeEnter: (to) =>
            to.query.view || to.query.date ? { name: "events-calendar", query: to.query } : true,
        },
        {
          path: "calendar",
          name: "events-calendar",
          component: () => import("@/views/events/EventsCalendar.vue"),
          meta: { title: "Calendar" },
        },
        {
          path: "programming",
          name: "events-programming",
          component: () => import("@/views/events/EventsProgramming.vue"),
          meta: { title: "Programming" },
        },
        {
          path: "conferences",
          name: "events-conferences",
          component: () => import("@/views/events/EventsConferences.vue"),
          meta: { title: "Conferences & Travel" },
        },
        {
          path: "venues",
          name: "events-venues",
          component: () => import("@/views/events/EventsVenues.vue"),
          meta: { title: "Venues" },
        },
      ],
    },
    {
      path: "/newsletters",
      component: () => import("@/views/newsletters/NewslettersHome.vue"),
      meta: { title: "Newsletters" },
      children: [
        {
          path: "",
          name: "newsletters-lists",
          component: () => import("@/views/newsletters/NewslettersLists.vue"),
        },
        {
          path: "cleanup",
          name: "newsletters-cleanup",
          component: () => import("@/views/newsletters/NewslettersCleanup.vue"),
          // The grid fills the window and scrolls inside it.
          meta: { title: "Cleanup", fixedLayout: true },
        },
      ],
    },
    {
      path: "/files",
      name: "files",
      component: () => import("@/views/FilesView.vue"),
      meta: { title: "Files" },
    },
    {
      path: "/files/match",
      name: "files-match",
      component: () => import("@/views/FilesMatch.vue"),
      meta: { title: "Match files" },
    },
    {
      path: "/search",
      name: "search",
      component: () => import("@/views/CommunitySearch.vue"),
      meta: { title: "Search" },
      props: (route) => ({ savedQuery: route.query.q }),
    },
    {
      // Id-only entry point — resolves the record's type + slug and
      // silently redirects to its canonical /r/:recordType/:slug URL.
      // Used where a caller only has a record id (e.g. event links).
      path: "/r/id/:id",
      name: "record-by-id",
      component: { render: () => null },
      beforeEnter: async (to) => {
        const id = Number(to.params.id)
        if (!Number.isFinite(id)) return { name: "root" }
        try {
          const { client } = await import("@/init/apollo")
          const { fetchRecordRouteById } = await import("@/common/hasura/records")
          const { data } = await client.query({
            query: fetchRecordRouteById,
            variables: { id },
            fetchPolicy: "cache-first",
          })
          const record = data?.record
          if (record?.type && record?.slug) {
            return {
              name: "record-show",
              params: { recordType: record.type, slug: record.slug },
            }
          }
        } catch (err) {
          console.error("Failed to resolve record by id:", err)
        }
        return { name: "root" }
      },
    },
    {
      path: "/r/:recordType/:slug",
      name: "record-show",
      component: () => import("@/views/RecordShow.vue"),
      meta: { title: (to) => `${to.params.recordType}: ${slugTitle(to.params.slug)}` },
      props: (route) => ({ recordType: route.params.recordType, slug: route.params.slug }),
    },
    {
      path: "/sources/unlinked/:recordType?",
      name: "sources",
      component: () => import("@/views/UnlinkedSources.vue"),
      meta: { title: "Unlinked Sources" },
      props: (route) => ({ recordType: route.params.recordType }),
    },
    {
      path: "/sources/:recordType/:sourceType/:sourceId",
      name: "community-source",
      component: () => import("@/views/CommunitySource.vue"),
      meta: { title: (to) => `${to.params.recordType} ${to.params.sourceType} Source` },
      props: (route) => ({
        recordType: route.params.recordType,
        sourceType: route.params.sourceType,
        sourceId: route.params.sourceId,
      }),
    },
    {
      path: "/list/:slug",
      name: "list-show",
      component: () => import("@/views/ListShow.vue"),
      meta: { title: (to) => slugTitle(to.params.slug), fixedLayout: true },
      props: (route) => ({ slug: route.params.slug }),
    },
    {
      path: "/pipelines",
      name: "pipelines",
      // No component — beforeEnter always redirects to first pipeline (or home).
      component: { render: () => null },
      beforeEnter: async () => {
        const { client } = await import("@/init/apollo")
        const { fetchPipelines } = await import("@/common/hasura/records")
        try {
          const { data } = await client.query({
            query: fetchPipelines,
            variables: { orderBy: [{ name: "asc" }] },
            fetchPolicy: "cache-first",
          })
          const records = data?.records || []
          const preferred = records.find((r) => r.slug === "member-pipeline")
          const target = preferred || records.find((r) => r.slug) || records[0]
          if (target?.slug) {
            return { name: "pipeline-show", params: { slug: target.slug } }
          }
        } catch (err) {
          console.error("Failed to resolve first pipeline:", err)
        }
        return { name: "root" }
      },
    },
    {
      path: "/pipeline/:slug",
      name: "pipeline-show",
      component: () => import("@/views/PipelineShow.vue"),
      meta: { title: (to) => slugTitle(to.params.slug), fixedLayout: true },
      props: (route) => ({ slug: route.params.slug }),
    },
    {
      path: "/pipeline/:pipelineSlug/:stageSlug",
      name: "pipeline-stage",
      component: () => import("@/views/ListShow.vue"),
      meta: {
        title: (to) => `${slugTitle(to.params.pipelineSlug)} - ${slugTitle(to.params.stageSlug)}`,
        fixedLayout: true,
      },
      props: (route) => ({ slug: route.params.stageSlug, pipelineSlug: route.params.pipelineSlug }),
    },
    {
      path: "/financing",
      name: "financing",
      component: () => import("@/views/FinancingDatabase.vue"),
      meta: { title: "Financing" },
    },
    {
      path: "/funds",
      name: "funds",
      component: () => import("@/views/FundList.vue"),
      meta: { title: "Funds" },
    },
    {
      path: "/funds/:slug",
      name: "fund-show",
      component: () => import("@/views/FundShow.vue"),
      meta: { title: (to) => `Fund ${slugTitle(to.params.slug)}` },
      props: (route) => ({ slug: route.params.slug }),
    },
    {
      path: "/members",
      name: "members",
      component: () => import("@/views/MemberList.vue"),
      meta: { title: "Members" },
    },
    {
      path: "/settings",
      name: "settings",
      component: () => import("@/views/UserSettings.vue"),
      meta: { title: "Settings" },
    },
    {
      path: "/tags",
      name: "tags",
      component: () => import("@/views/CommunityTags.vue"),
      meta: { title: "Tags" },
      props: (route) => ({
        savedFilter: route.query.filter,
      }),
    },
    {
      path: "/tags/:slug",
      name: "community-tag",
      component: () => import("@/views/TagShow.vue"),
      meta: { title: (to) => slugTitle(to.params.slug) },
      props: (route) => ({ slug: route.params.slug }),
    },
    // ---- Admin routes ----
    {
      path: "/admin",
      name: "admin",
      component: () => import("@/views/admin/AdminPanel.vue"),
      meta: { title: "Admin", fixedLayout: true },
      redirect: { name: "admin-types" },
      beforeEnter: () => (useUserStore().isAdmin ? true : { name: "home" }),
      children: [
        // The Users and Access panels edited the users and grant tables,
        // which nothing reads any more (Phase 7 PR C): people and roles live
        // in Stytch. Old links land on the admin home instead of a 404.
        { path: "users", redirect: { name: "admin-types" } },
        { path: "access", redirect: { name: "admin-types" } },
        { path: "grants", redirect: { name: "admin-types" } },
        {
          path: "entities",
          name: "admin-entities",
          component: () => import("@/views/admin/EntityRelations.vue"),
          meta: { title: "Entity Relations", nav: { label: "Entities", icon: "database" } },
        },
        {
          path: "types",
          name: "admin-types",
          component: () => import("@/views/admin/TypesManager.vue"),
          meta: { title: "Record Types", nav: { label: "Types", icon: "shapes" } },
        },
        {
          path: "schemas",
          name: "admin-schemas",
          component: () => import("@/views/admin/SchemaManager.vue"),
          meta: { title: "Schemas", nav: { label: "Schemas", icon: "layer-group" } },
        },
        {
          path: "links",
          name: "admin-links",
          component: () => import("@/views/admin/LinksManager.vue"),
          meta: { title: "Record Links", nav: { label: "Record Links", icon: "link" } },
        },
        {
          path: "times",
          name: "admin-times",
          component: () => import("@/views/admin/TimesManager.vue"),
          meta: { title: "Record Times", nav: { label: "Record Times", icon: "clock" } },
        },
        {
          path: "contacts",
          name: "admin-contacts",
          component: () => import("@/views/admin/ContactsManager.vue"),
          meta: { title: "Record Contacts", nav: { label: "Record Contacts", icon: "envelope" } },
        },
        {
          path: "records/:type?",
          name: "admin-records",
          component: () => import("@/views/admin/RecordsBrowser.vue"),
          meta: { title: "Browse Records", nav: { label: "Browse Records", icon: "table" } },
        },
        {
          path: "data-quality/:type?",
          name: "admin-data-quality",
          component: () => import("@/views/admin/DataQualityManager.vue"),
          meta: { title: "Data Quality", nav: { label: "Data Quality", icon: "check-circle" } },
        },
        {
          path: "duplicate-records/:type?",
          name: "admin-duplicate-records",
          component: () => import("@/views/admin/DuplicateRecordsManager.vue"),
          meta: { title: "Duplicate Records", nav: { label: "Duplicate Records", icon: "clone" } },
        },
        {
          path: "linkedin-import",
          name: "admin-linkedin-import",
          component: () => import("@/views/admin/LinkedInImportManager.vue"),
          meta: {
            title: "LinkedIn Import",
            nav: { label: "LinkedIn Import", icon: "address-book" },
          },
        },
        {
          path: "location-tags",
          name: "admin-location-tag-dedup",
          component: () => import("@/views/admin/LocationTagDedupManager.vue"),
          meta: {
            title: "Location Tag Duplicates",
            nav: { label: "Location Tags", icon: "globe" },
          },
        },
        {
          path: "audit-log",
          name: "admin-audit-log",
          component: () => import("@/views/admin/AuditLog.vue"),
          meta: { title: "Audit Log", nav: { label: "Audit Log", icon: "magnifying-glass" } },
        },
        {
          path: "components",
          name: "admin-components",
          component: () => import("@/views/admin/FieldComponentShowcase.vue"),
          meta: { title: "Field Components", nav: { label: "Field Components", icon: "grip" } },
        },
        {
          path: "blocks",
          name: "admin-blocks",
          component: () => import("@/views/admin/BlocksShowcase.vue"),
          meta: {
            title: "Layout Blocks",
            nav: { label: "Layout Blocks", icon: "table-cells-large" },
          },
        },
      ],
    },
  ],
})

// ensure user is logged in and stores are loaded before showing them any pages
// Whether the users-load failure has been toasted since the last success.
let usersWarned = false

router.beforeEach(async (to) => {
  // Heavybit Identity pages serve people who may have no Auger account at
  // all; they must never trigger the Auger login or load the user stores.
  if (to.meta.identity) return true

  if (auth.enabled) {
    // Wait for the SDK to hydrate (possibly a silent refresh-token exchange)
    // before checking auth state — checking early was the Safari cold-load
    // redirect bounce.
    await auth.ready()
    if (!auth.isAuthenticated()) {
      auth.login(to.fullPath)
      return false // abort navigation; browser is redirecting to the sign-in page
    }

    // Signed in, but the session names no person record: a sign-in that did not
    // finish, not a waitlist. The sign-in exchange gives every member a person
    // before the first token (functions/public/auth-session.js, step 2c), so
    // this is a session older than that guarantee, or a token the app cannot
    // read. It is shown as the callback's "Sign-in failed" state, whose "Try
    // again" restarts the sign-in. Checked first, for everyone, BEFORE anything
    // below runs.
    if (auth.principal()?.identity.auger_person_id === undefined) {
      return {
        path: "/identity/callback",
        query: { reason: "no_person_record", return_to: to.fullPath },
      }
    }

    // Signed in, but with no access to THIS app yet (class `network`, holding
    // no grant that Auger recognises — see identity/waitlist.ts, where today
    // that means any `network` person at all). Send them to the waitlist page
    // BEFORE anything below runs: the `network` Hasura role can read two
    // columns of its own `users` row and nothing else, so identify() and every
    // store fetch below would fail, which on 2026-09-15 left the app hanging
    // on "Signing you in" with nothing on screen. Decided on class + roles
    // from the token, never by catching the Apollo error — that string is not
    // a contract, and by the time it is thrown the stores have already
    // started.
    if (isWaitlisted(auth.principal()?.permissions.roles)) return WAITLIST_PATH
  }

  // The catalog (schemas, types) is global, not the user's: it needs only the
  // session above, so it starts alongside the user's identity rather than
  // after it. A returning visit has it from IndexedDB at once. Idempotent,
  // like identify(). It is below the guards above because these are the first
  // things that talk to Apollo, and identity pages must stay store-free.
  const catalogReady = startCatalog()
  // Awaited below; this only keeps a failure from reporting as unhandled meanwhile.
  catalogReady.catch(() => {})
  userState.identify()
  // The user list loads in the background; the views reading it are reactive.
  // A failure is retried on the next navigation, and said once.
  useUsersStore()
    .fetch()
    .then(
      () => {
        usersWarned = false
      },
      (err: unknown) => {
        console.error("users fetching error", err)
        if (!usersWarned) toast.error("Couldn't load the team list — people may show as blank.")
        usersWarned = true
      }
    )

  // wait for user identity before route guards check roles
  await userState.waitForLogin()
  await catalogReady

  // Redirect to user's preferred landing page when hitting the root.
  if (to.path === "/") {
    const landing = useUserStore().userData?.default_landing_path
    if (landing && landing !== "/" && router.resolve(landing).matched.length > 0) {
      return landing
    }
  }

  return true
})

// update page title after navigation
router.afterEach((to) => {
  if (to.meta.identity) {
    document.title = "Heavybit"
    return
  }
  const base = "Heavybit Auger"
  let title = to.meta?.title
  if (typeof title === "function") {
    title = title(to)
  }
  document.title = title ? `${title} - ${base}` : base
})

export default router
